import React from 'react';
import { Link } from 'react-router-dom';
import { LegalList, LegalSection } from './LegalParts';
import { ROUTES } from './siteNav';

const COOKIE_TYPES = [
  { term: 'Required cookies', body: 'used to perform essential website functions.' },
  {
    term: 'Analytics cookies',
    body: 'to better understand how you and others use our websites so that we can make them better, and so third parties can improve their own products and services.',
  },
  { term: 'Advertising cookies', body: 'to show you ads that are relevant to you.' },
  {
    term: 'Social media cookies',
    body: 'designed to show you ads and content based on your social media profiles and activities on our websites.',
  },
];

const CORE_MARKS = [
  { term: 'NuVola AI®', body: 'AI technology platform' },
  { term: 'No / SCAM™', body: 'anti-scam detection software' },
  { term: 'NuVola AI Copilot™', body: 'AI assistant technology' },
  { term: 'Scammer Confidence Index™', body: 'risk assessment system' },
];

const PRODUCT_MARKS = [
  { term: 'Digital Predator Protection™', body: 'security service' },
  { term: 'Behavioral Modeling Engine™', body: 'AI analysis technology' },
  { term: 'Responsible AI Framework™', body: 'ethical AI standard' },
  { term: 'No / SCAM Intelligence™', body: 'threat intelligence service' },
];

/**
 * The legal documents, all reachable from /legal. Each keeps its own URL so
 * links to a specific policy stay stable and shareable.
 */
export const LEGAL_DOCUMENTS = [
  {
    id: 'terms',
    label: 'Terms of Service',
    path: ROUTES.terms,
    title: 'Terms of Service',
    body: (
      <LegalSection heading="Ownership of Site; Agreement to Terms of Use">
        <p>
          These Terms and Conditions of Use (the &ldquo;Terms of Use&rdquo;) apply to the NuVola AI
          web site located at www.nuvolaai.com, and all associated sites linked to www.nuvolaai.com
          by NuVola AI, its subsidiaries and affiliates, including NuVola AI sites around the world
          (collectively, the &ldquo;Site&rdquo;). The Site is the property of NuVola AI Inc.
          (&ldquo;NuVola AI&rdquo;) and its licensors. BY USING THE SITE, YOU AGREE TO THESE TERMS
          OF USE; IF YOU DO NOT AGREE, DO NOT USE THE SITE.
        </p>
        <p>
          NuVola AI reserves the right, at its sole discretion, to change, modify, add or remove
          portions of these Terms of Use at any time. It is your responsibility to check these Terms
          of Use periodically for changes. Your continued use of the Site following the posting of
          changes will mean that you accept and agree to the changes. As long as you comply with
          these Terms of Use, NuVola AI grants you a personal, non-exclusive, non-transferable,
          limited privilege to enter and use the Site.
        </p>
      </LegalSection>
    ),
  },
  {
    id: 'trademark',
    label: 'Trademark & Copyright',
    path: ROUTES.trademark,
    title: 'Trademark Information',
    body: (
      <>
        <LegalSection heading="What is a trademark?">
          <p>
            A trademark can be a word, phrase, symbol, or design that distinguishes the source of
            the goods or services. Also, as trade dress, it can be the appearance of a product or
            its packaging &mdash; including size, shape, color, texture, graphics, and appearance
            (for example, a retail store or website).
          </p>
          <p>The following is a non-exhaustive list of NuVola AI trademarks and service marks.</p>
        </LegalSection>

        <LegalSection heading="Usage guidelines">
          <p>
            When using the marks in publications that will be distributed only in the United States,
            include the appropriate &trade;, &#8480;, or &reg; symbol on first use. For publications
            that will be distributed outside the United States, do not include trademark symbols.
          </p>
          <p>
            The list also includes a suggested generic term for each trademark. For all
            publications, include an appropriate generic term after the trademark the first time it
            appears. Thereafter, the generic term should appear frequently with the trademark.
            (These generic terms are only suggestions, and there may be other words that are equally
            appropriate.)
          </p>
        </LegalSection>

        <LegalSection heading="NuVola AI trademarks">
          <p className="font-semibold text-black">Core brand marks</p>
          <LegalList items={CORE_MARKS} />
          <p className="mt-5 font-semibold text-black">Product and service marks</p>
          <LegalList items={PRODUCT_MARKS} />
        </LegalSection>

        <LegalSection heading="Important notice">
          <p>
            The absence of a product or service name or logo from this list does not constitute a
            waiver of NuVola AI&rsquo;s trademark or other intellectual property rights concerning
            that name or logo.
          </p>
          <p>
            For questions about the proper use of NuVola AI trademarks, or to request permission to
            use our trademarks, please contact our legal team.
          </p>
          <p>
            All trademarks and service marks are the property of NuVola AI Inc. and are protected
            under applicable trademark laws.
          </p>
        </LegalSection>
      </>
    ),
  },
  {
    id: 'cookies',
    label: 'Cookies',
    path: ROUTES.cookies,
    title: 'Cookies and Similar Technologies',
    body: (
      <>
        <LegalSection heading="What are cookies?">
          <p>
            Cookies are small text files placed on your device to store data that can be recalled by
            a web server in the domain that placed the cookie. We use cookies and similar
            technologies for storing and honoring your preferences and settings, enabling you to
            sign in, combating fraud, analyzing how our products perform, and fulfilling other
            legitimate purposes. We also use &ldquo;web beacons&rdquo; to help deliver cookies and
            gather usage and performance data.
          </p>
        </LegalSection>

        <LegalSection heading="Types of cookies">
          <p>Third party cookies may include:</p>
          <LegalList items={COOKIE_TYPES} />
          <p>
            Where required, we obtain your consent prior to placing or using optional cookies that
            are not (i) strictly necessary to provide the website, or (ii) for the purpose of
            facilitating a communication.
          </p>
        </LegalSection>

        <LegalSection heading="Third-party cookies and services">
          <p>
            For information about our use of third-party cookies, web beacons, analytics services
            and other similar technologies on our websites and services &mdash; including a list of
            the third parties that set cookies on our websites, and service providers acting on our
            behalf &mdash; please see our third party cookie inventory.
          </p>
          <p>
            On some of our websites, a list of third parties is available directly on the site. The
            third parties on these sites may not be included in the list on our third party cookie
            inventory.
          </p>
        </LegalSection>

        <LegalSection heading="Your cookie controls">
          <p>
            You have a variety of tools to control the data collected by cookies, web beacons, and
            similar technologies. For example, you can use controls in your internet browser to
            limit how the websites you visit are able to use cookies, and to withdraw your consent
            by clearing or blocking cookies.
          </p>
          <p>
            This cookies policy is part of NuVola AI&rsquo;s overall privacy commitment. For more
            about how we collect, use, and protect your data, see{' '}
            <Link to={ROUTES.dataPrivacy} className="text-[#0A6EA8] underline">
              Data Privacy
            </Link>
            .
          </p>
        </LegalSection>
      </>
    ),
  },
];
